import React, { useEffect, useMemo, useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import RichardMonogram from '~/pages/AskRichardUnified/chat/RichardMonogram';

import { useGetLensColumnValues } from '~/hooks/apis/reporting/useLensColumnValues';

import {
  type LensCalendar,
  type LensDataset,
  type LensFilter,
  type LensFilterValues,
  type LensPeriod,
  type LensResolvedPeriod,
} from '~/types/leLens';

import { clearDeclaredFilters, effectiveDeclaredFilters, isActiveDeclaredValue } from './NativeLensFilter';
import PeriodRows from './PeriodRows';
import {
  type LensRange,
  type LensSelection,
  distinctValues,
  hasSelection,
  inferDimensions,
  looksLikeDate,
  numericColumns,
  valueExtent,
} from './cardView';
import { declaredDatasetDescription, declaredDatasetLabel, humaniseFilterLabel } from './filterLabels';
import { governedDisplayDataset, useGovernedDisplayMetadata } from './governedDisplayMetadata';
import {
  announceLensFilter,
  describeClearAllAnnouncement,
  describeRangeAnnouncement,
  describeSelectionAnnouncement,
} from './lensAnnouncer';
import { useDebouncedValue } from './useDebouncedValue';

export type FilterSource = {
  cardId: string;
  title: string;
  rows: Array<Record<string, unknown>>;
  columns: Array<string>;
};

type DatasetFilterBarProps = {
  savedFilters?: React.ReactNode;
  /** Reveals the existing Lens conversation without submitting a question. */
  onAskRichard?: () => void;
  lensId?: string;
  queryFilters?: LensFilterValues;
  /** Declared board filters are rendered by the parent but share this natural-language control. */
  declaredFilters?: Array<LensFilter>;
  declaredFilterValues?: LensFilterValues;
  declaredFilterOptions?: Record<string, Array<string>>;
  onChangeDeclaredFilters: (values: LensFilterValues) => void;
  /** Every card that returned rows: filtering needs a shared field, not a shared query. */
  sources: Array<FilterSource>;
  /** Successful card reads, including empty results, independently of option rows. */
  coverageCardCount?: number;
  /** Dataset dimensions still seed the default, where consolidation set any. */
  datasets: Array<LensDataset>;
  /** Columns chosen for the dashboard. Absent means offer a guess. */
  filterColumns?: Array<string>;
  onSetFilterColumns: (columns: Array<string>) => void;
  /** Set while the Lens's Configure menu has opened the column picker. */
  columnPickerAnchor?: HTMLElement | null;
  onCloseColumnPicker?: () => void;
  /** Card titles a column reaches, keyed by column. */
  affectedCards: Record<string, Array<string>>;
  selections: LensSelection;
  onChange: (selection: LensSelection) => void;
  /** Measure ranges, keyed by column like the value selections. */
  ranges: Record<string, LensRange>;
  onChangeRanges: (ranges: Record<string, LensRange>) => void;
  /**
   * The dashboard period is committed explicitly and bounds every dataset read.
   */
  /** The window and comparison in force: the board's, with the viewer's own keys over it. */
  period: LensPeriod;
  boardPeriod: LensPeriod;
  resolvedPeriod?: LensResolvedPeriod;
  onChangePeriod: (patch: Partial<LensPeriod> | null) => void;
  onSaveBoardPeriod: (period: LensPeriod) => Promise<void>;
  calendar?: LensCalendar;
  /** Datasets the period reaches, by name. Empty means nothing is date-bound. */
  datedDatasets: Array<string>;
  /** True while the new period's queries are still running. */
  isReloading?: boolean;
  onClearAll: () => void;
  canEdit: boolean;
};

/**
 * Select defaults to the selectedMenu variant, which anchors the popup on the
 * chosen item and so centres it over the control. Anchoring below the box keeps
 * the list where a reader expects it, and a capped height makes a long one
 * scroll rather than run off the page.
 */
const DROPDOWN_PROPS = {
  anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
  transformOrigin: { horizontal: 'left', vertical: 'top' },
  variant: 'menu',
  PaperProps: { sx: { maxHeight: 360 } },
} as const;

/**
 * Dataset selections reach the server before aggregation. Options start from
 * loaded rows and fetch the complete bounded dimension list when opened.
 *
 * Controls are keyed by column name across datasets: three datasets carrying
 * "vendor" is one filter to a reader, and picking a vendor moves all of them.
 *
 * The bar sits directly on the canvas as a single compact row with a bottom
 * hairline, not inside a filled panel: a filter row is a toolbar, not its own
 * screen band.
 */
export default function DatasetFilterBar({
  savedFilters,
  onAskRichard,
  lensId,
  queryFilters = {},
  declaredFilters = [],
  declaredFilterValues = {},
  declaredFilterOptions = {},
  onChangeDeclaredFilters,
  sources,
  coverageCardCount,
  datasets,
  filterColumns,
  columnPickerAnchor,
  onCloseColumnPicker,
  onSetFilterColumns,
  affectedCards,
  selections,
  onChange,
  ranges,
  onChangeRanges,
  period,
  boardPeriod,
  resolvedPeriod,
  onChangePeriod,
  calendar,
  onSaveBoardPeriod,
  datedDatasets,
  isReloading,
  onClearAll,
  canEdit,
}: DatasetFilterBarProps) {
  // Opened from the Lens's Configure menu rather than from a control beside
  // the filters: which columns are offered is a decision about the Lens, not
  // one a reader makes while reading it.
  const [picking, setPicking] = useState<{ anchor: HTMLElement } | null>(null);
  // Search text per column: a vendor list runs to hundreds of values, and
  // scrolling one is not a way to find anything. The typed value updates
  // immediately so the field feels responsive; only the debounced value below
  // drives the (potentially large) options scan, so a keystroke does not
  // rescan every loaded row of every card.
  const [search, setSearch] = useState<Record<string, string>>({});
  const [fieldSearch, setFieldSearch] = useState('');
  // Which filter's menu is open, and which one is showing its tooltip. The
  // tooltip wraps the whole control, so without this the pointer that opened
  // the menu is still counted as hovering and the hint sits over the options
  // the user opened the menu to read.
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [hintFor, setHintFor] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search, 150);
  const governedDimensions = useMemo(
    () => [
      ...new Set(
        datasets
          .filter((dataset) => dataset.source?.kind === 'governed')
          .flatMap(
            (dataset) =>
              dataset.columns?.filter((field) => field.kind === 'dimension').map((field) => field.name) ?? [],
          ),
      ),
    ],
    [datasets],
  );
  const optionDataset = datasets.find(
    (dataset) =>
      dataset.source?.kind === 'governed' &&
      dataset.columns?.some((field) => field.kind === 'dimension' && field.name === openMenu),
  );
  const optionQuery = useGetLensColumnValues({
    lensId,
    datasetId: optionDataset?.id,
    column: openMenu ?? undefined,
    filters: queryFilters,
  });

  // A dataset's own declared label wins; failing that the column name is
  // humanised, so "product bk" reads as "Product" everywhere the column
  // appears: the dropdown label, its search placeholder and the picker menu.
  const displayMetadata = useGovernedDisplayMetadata();
  const displayDatasets = datasets.map((dataset) => governedDisplayDataset(dataset, displayMetadata)!);
  const label = (column: string) => humaniseFilterLabel(column, declaredDatasetLabel(column, displayDatasets));
  const description = (column: string) => declaredDatasetDescription(column, displayDatasets);

  // Default: what the results suggest, plus anything consolidation named.
  // Memoised on the props that actually determine it: typing in the search
  // box above sets local `search` state, which re-renders this component,
  // and without this these scans over every loaded row of every card would
  // otherwise redo on every keystroke rather than only when the underlying
  // data or column choice changes.
  const suggested = useMemo(
    () =>
      [
        ...new Set([
          ...sources.flatMap((source) => inferDimensions(source.rows, source.columns)),
          ...datasets.flatMap((dataset) => dataset.dimensions ?? []),
        ]),
      ].filter((column) => sources.some((source) => source.columns.includes(column))),
    [sources, datasets],
  );

  // An active selection must remain visible even when the narrowed backend
  // response no longer returns that filter-only field. Otherwise applying a
  // filter makes its own control disappear and leaves Clear all as the only
  // route back.
  const chosenColumns = useMemo(
    () =>
      [...new Set([...(filterColumns ?? suggested), ...Object.keys(selections), ...Object.keys(ranges)])].filter(
        (column) =>
          column in selections ||
          column in ranges ||
          !declaredFilters.some((filter) => filter.column?.toLowerCase() === column.toLowerCase()),
      ),
    [filterColumns, selections, ranges, suggested, declaredFilters],
  );

  // Every column any card carries, for the picker.
  const availableColumns = useMemo(
    () => [...new Set([...sources.flatMap((source) => source.columns), ...governedDimensions])].sort(),
    [sources, governedDimensions],
  );

  // One control per column name, pooling values from every card that knows it.
  const columns = useMemo(() => {
    const byColumn = new Map<string, Set<string>>();
    for (const source of sources) {
      for (const column of chosenColumns) {
        if (!source.columns.includes(column)) {
          continue;
        }
        if (looksLikeDate(source.rows, column) || numericColumns(source.rows, [column]).length) {
          continue;
        }
        const existing = byColumn.get(column) ?? new Set<string>();
        distinctValues(source.rows, column).forEach((value) => existing.add(value));
        byColumn.set(column, existing);
      }
    }
    for (const column of chosenColumns) {
      if (!governedDimensions.includes(column)) continue;
      const values = byColumn.get(column) ?? new Set<string>();
      (selections[column] ?? []).forEach((value) => values.add(value));
      if (openMenu === column) optionQuery.data?.values.forEach((value) => values.add(value));
      byColumn.set(column, values);
    }
    // Workspace and older governed datasets can omit a dimensions contract.
    // Preserve the selected values for those too, so their active control does
    // not vanish after the result is narrowed.
    for (const [column, selected] of Object.entries(selections)) {
      if (!chosenColumns.includes(column)) continue;
      const values = byColumn.get(column) ?? new Set<string>();
      selected.forEach((value) => values.add(value));
      byColumn.set(column, values);
    }
    return [...byColumn.entries()].map(([column, values]) => ({
      column,
      options: [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    }));
  }, [sources, chosenColumns, governedDimensions, selections, openMenu, optionQuery.data]);

  // Measures are narrowed by range rather than by picking values, and their
  // span comes from the rows already loaded.
  const measures = useMemo(() => {
    const result = new Map<string, LensRange>();
    for (const source of sources) {
      // Measures stay opt-in: only a column somebody chose gets a range.
      for (const column of chosenColumns.filter(
        (candidate) => source.columns.includes(candidate) && numericColumns(source.rows, [candidate]).length > 0,
      )) {
        const extent = valueExtent(source.rows, column);
        const existing = result.get(column);
        result.set(column, {
          min: existing?.min === undefined ? extent.min : Math.min(existing.min, extent.min ?? existing.min),
          max: existing?.max === undefined ? extent.max : Math.max(existing.max, extent.max ?? existing.max),
        });
      }
    }
    return result;
  }, [sources, chosenColumns]);

  const totalCards = coverageCardCount ?? sources.length;

  const handleClearAll = () => {
    onClearAll();
    onChangeDeclaredFilters(clearDeclaredFilters(declaredFilterValues, declaredFilters));
    announceLensFilter(describeClearAllAnnouncement(totalCards));
  };

  const hasDatedCards = datedDatasets.length > 0;
  const askRichardAction = onAskRichard ? (
    <Stack alignItems="center" spacing={0.75} sx={{ py: 1, width: '100%' }}>
      <Button
        aria-label="askRichard"
        onClick={onAskRichard}
        size="small"
        startIcon={<RichardMonogram sx={{ fontSize: 20 }} />}
        sx={{ minHeight: 32 }}
        variant="outlined"
      >
        askRichard
      </Button>
      <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.4, textAlign: 'center' }}>
        Filter in plain language
      </Typography>
    </Stack>
  ) : null;

  // An editor can configure dimensions elsewhere, but an empty filter shell
  // does not help anyone understand or use the dashboard.
  if (!savedFilters && !columns.length && !measures.size && !hasDatedCards && !availableColumns.length) {
    return (
      <Stack spacing={1}>
        <Typography color="text.secondary" variant="body2">
          These cards use fixed query results. To change their dates or filters, open a card’s Source tab or ask
          askRichard for a new view.
        </Typography>
        {askRichardAction}
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={2} sx={{ minWidth: 0, width: '100%' }}>
        {askRichardAction}
        {hasDatedCards && (
          <PeriodRows
            appliesTo={datedDatasets}
            boardPeriod={boardPeriod}
            calendar={calendar}
            canEdit={canEdit}
            isReloading={isReloading}
            onChange={onChangePeriod}
            onSaveBoard={onSaveBoardPeriod}
            period={period}
            resolved={resolvedPeriod}
          />
        )}

        <Divider />
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="subtitle2">Filter values</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 11.5 }}>
              Changes apply to linked cards.
            </Typography>
          </Box>
          {canEdit && availableColumns.length > 0 && (
            <Button
              onClick={(event) => {
                setFieldSearch('');
                setPicking({ anchor: event.currentTarget });
              }}
              size="small"
              variant="outlined"
            >
              Add filter
            </Button>
          )}
        </Stack>
        {savedFilters}
        {!columns.length && !measures.size && !savedFilters && (
          <Typography color="text.secondary" variant="body2">
            No filters yet. Add a field to narrow your results.
          </Typography>
        )}
        {columns.map(({ column, options }) => {
          // Absent means every value: the control opens fully ticked.
          const chosen = selections[column];
          const allTicked = chosen === undefined;
          const cards = affectedCards[column] ?? [];
          // Debounced so a fast typist does not rescan the option list once
          // per keystroke; the field itself still echoes every keystroke.
          const term = (debouncedSearch[column] ?? '').trim().toLowerCase();
          const visible = term ? options.filter((option) => option.toLowerCase().includes(term)) : options;

          return (
            <Tooltip
              key={column}
              onClose={() => setHintFor(null)}
              onOpen={() => setHintFor(column)}
              open={hintFor === column && openMenu !== column}
              title={cards.length > 0 ? `Affects ${cards.join(', ')}` : ''}
            >
              <FormControl
                size="small"
                sx={{
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <InputLabel id={`le-lens-filter-${column}`}>{label(column)}</InputLabel>
                <Select
                  MenuProps={DROPDOWN_PROPS}
                  label={label(column)}
                  labelId={`le-lens-filter-${column}`}
                  multiple
                  onChange={() => {
                    /* Every change is handled on the items, so the tick state
                       can distinguish all from none. */
                  }}
                  onClose={() => setOpenMenu(null)}
                  onOpen={() => setOpenMenu(column)}
                  renderValue={() => (chosen === undefined ? 'All' : chosen.length === 0 ? 'None' : chosen.join(', '))}
                  // Absent means everything, so show every option ticked.
                  value={chosen ?? options}
                >
                  {options.length > 8 && (
                    // Select swallows typing for its own jump-to-letter, so the
                    // field has to keep its keystrokes to itself.
                    <Box
                      onKeyDown={(event) => {
                        if (event.key !== 'Escape') event.stopPropagation();
                      }}
                      sx={{ px: 1.5, py: 0.5 }}
                    >
                      <TextField
                        autoFocus
                        fullWidth
                        onChange={(event) => setSearch((current) => ({ ...current, [column]: event.target.value }))}
                        onClick={(event) => event.stopPropagation()}
                        placeholder={`Search ${label(column)}`}
                        size="small"
                        value={search[column] ?? ''}
                      />
                    </Box>
                  )}
                  <MenuItem
                    onClick={(event) => {
                      event.preventDefault();
                      const updated = { ...selections };
                      // With a search running, select-all means these matches,
                      // so searching then selecting is a way to pick a group.
                      if (term) {
                        const kept = new Set([...(chosen ?? options), ...visible]);
                        updated[column] = options.filter((option) => kept.has(option));
                      } else if (allTicked) {
                        // Ticked means all: unticking it selects nothing, which
                        // shows nothing, rather than quietly meaning everything.
                        updated[column] = [];
                      } else {
                        delete updated[column];
                      }
                      onChange(updated);
                      announceLensFilter(describeSelectionAnnouncement(label(column), updated[column], cards.length));
                    }}
                    value="__all"
                  >
                    <Checkbox
                      checked={allTicked}
                      indeterminate={!allTicked && (chosen?.length ?? 0) > 0}
                      size="small"
                    />
                    <ListItemText primary={term ? `Add these ${visible.length}` : 'Select all'} />
                  </MenuItem>
                  <Divider />
                  {visible.map((option) => {
                    const ticked = (chosen ?? options).includes(option);
                    return (
                      <MenuItem
                        key={option}
                        onClick={(event) => {
                          event.preventDefault();
                          const from = chosen ?? options;
                          const next = ticked ? from.filter((entry) => entry !== option) : [...from, option];
                          const updated = { ...selections };
                          if (next.length === options.length) {
                            delete updated[column];
                          } else {
                            updated[column] = next;
                          }
                          onChange(updated);
                          announceLensFilter(
                            describeSelectionAnnouncement(label(column), updated[column], cards.length),
                          );
                        }}
                        value={option}
                      >
                        <Checkbox checked={ticked} size="small" />
                        <ListItemText primary={option} />
                        <Button
                          onClick={(event) => {
                            event.stopPropagation();
                            onChange({ ...selections, [column]: [option] });
                            announceLensFilter(describeSelectionAnnouncement(label(column), [option], cards.length));
                          }}
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          Only
                        </Button>
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText
                  component="div"
                  sx={{ alignItems: 'center', display: 'flex', gap: 0.5, lineHeight: 1.35, ml: 0 }}
                >
                  {cards.length} of {totalCards} cards
                  {description(column) ? (
                    <Tooltip title={description(column)}>
                      <InfoOutlinedIcon aria-label={`About ${label(column)}`} sx={{ fontSize: 15 }} />
                    </Tooltip>
                  ) : null}
                </FormHelperText>
              </FormControl>
            </Tooltip>
          );
        })}

        {[...measures.entries()].map(([column, extent]) => (
          <MeasureRangeControl
            cards={affectedCards[column] ?? []}
            extent={extent}
            key={column}
            label={label(column)}
            onChangeCommitted={(patch) => {
              const updated = { ...ranges };
              if (patch.min === undefined && patch.max === undefined) {
                delete updated[column];
              } else {
                updated[column] = patch;
              }
              onChangeRanges(updated);
            }}
            range={ranges[column] ?? {}}
          />
        ))}

        {(hasSelection(selections) ||
          Object.keys(ranges).length > 0 ||
          Object.values(effectiveDeclaredFilters(declaredFilters, declaredFilterValues)).some(
            isActiveDeclaredValue,
          )) && (
          <Chip label="Clear all" onClick={handleClearAll} onDelete={handleClearAll} size="small" variant="outlined" />
        )}
      </Stack>

      <Menu
        anchorEl={picking?.anchor ?? columnPickerAnchor ?? null}
        onClose={() => {
          setPicking(null);
          onCloseColumnPicker?.();
        }}
        open={Boolean(picking || columnPickerAnchor)}
        slotProps={{ paper: { sx: { width: 360, maxWidth: 'calc(100vw - 32px)', maxHeight: 440 } } }}
      >
        <Box sx={{ px: 1.5, py: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Find a field…"
            inputProps={{ 'aria-label': 'Find a filter field' }}
            value={fieldSearch}
            onChange={(event) => setFieldSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Escape') event.stopPropagation();
            }}
          />
        </Box>
        {!availableColumns.some((column) =>
          `${label(column)} ${column}`.toLowerCase().includes(fieldSearch.toLowerCase()),
        ) && <MenuItem disabled>No matching fields</MenuItem>}
        {availableColumns
          .filter((column) => `${label(column)} ${column}`.toLowerCase().includes(fieldSearch.toLowerCase()))
          .map((column) => {
            const carrier = sources.find((source) => source.columns.includes(column));
            const rows = carrier?.rows ?? [];
            const isShown = chosenColumns.includes(column);
            // Say what ticking a column will get you, so a date does not look
            // like a control that failed to appear.
            const isDate = looksLikeDate(rows, column);
            const isMeasure = !isDate && numericColumns(rows, [column]).length > 0;

            return (
              <MenuItem
                disabled={isDate}
                key={column}
                onClick={() =>
                  onSetFilterColumns(
                    isShown ? chosenColumns.filter((name) => name !== column) : [...chosenColumns, column],
                  )
                }
              >
                <Checkbox checked={isShown && !isDate} disabled={isDate} size="small" />
                <ListItemText
                  primary={label(column)}
                  secondary={
                    isDate
                      ? 'Use the reporting period above'
                      : isMeasure
                        ? 'Filter displayed values'
                        : `${affectedCards[column]?.length ?? 0} of ${totalCards} cards`
                  }
                />
              </MenuItem>
            );
          })}
        <Divider />
        <MenuItem
          onClick={() => {
            onSetFilterColumns([]);
            setPicking(null);
          }}
        >
          <ListItemText primary="Remove all fields" />
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * Comparison is deliberately an opt-in analytical lens, not a global display
 * toggle. It adds a quiet delta beneath KPI values while leaving the current
 * period as the dominant reading of every chart and table.
 */
function MeasureRangeControl({
  label: fieldLabel,
  range,
  extent,
  cards,
  onChangeCommitted,
}: {
  label: string;
  range: LensRange;
  extent: LensRange;
  cards: Array<string>;
  onChangeCommitted: (patch: LensRange) => void;
}) {
  const [minText, setMinText] = useState(range.min === undefined ? '' : String(range.min));
  const [maxText, setMaxText] = useState(range.max === undefined ? '' : String(range.max));

  // Follow a range cleared elsewhere, such as "Clear filters", without
  // fighting a value still being typed here.
  useEffect(() => {
    setMinText(range.min === undefined ? '' : String(range.min));
  }, [range.min]);
  useEffect(() => {
    setMaxText(range.max === undefined ? '' : String(range.max));
  }, [range.max]);

  const debouncedMin = useDebouncedValue(minText, 400);
  const debouncedMax = useDebouncedValue(maxText, 400);

  useEffect(() => {
    const parsedMin = debouncedMin.trim() === '' ? undefined : Number(debouncedMin);
    const parsedMax = debouncedMax.trim() === '' ? undefined : Number(debouncedMax);
    if (parsedMin === range.min && parsedMax === range.max) {
      return;
    }
    onChangeCommitted({ min: parsedMin, max: parsedMax });
    announceLensFilter(describeRangeAnnouncement(fieldLabel, { min: parsedMin, max: parsedMax }, cards.length));
    // Committing intentionally reacts only to the settled, debounced text;
    // range/cards/onChangeCommitted are read for comparison, not to retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMin, debouncedMax]);

  return (
    <Tooltip title={cards.length > 0 ? `Affects ${cards.join(', ')}` : ''}>
      <Box>
        {/* The measure is named once above, so neither input has to carry a
            label long enough to be truncated. */}
        <Typography sx={{ display: 'block', mb: 0.25, ml: 0.5 }} variant="caption">
          {fieldLabel}
        </Typography>
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <TextField
            aria-label={`${fieldLabel} from`}
            onChange={(event) => setMinText(event.target.value)}
            placeholder={extent.min === undefined ? 'min' : String(Math.floor(extent.min))}
            size="small"
            sx={{ width: 96 }}
            type="number"
            value={minText}
          />
          <Typography color="text.secondary" variant="caption">
            to
          </Typography>
          <TextField
            aria-label={`${fieldLabel} to`}
            onChange={(event) => setMaxText(event.target.value)}
            placeholder={extent.max === undefined ? 'max' : String(Math.ceil(extent.max))}
            size="small"
            sx={{ width: 96 }}
            type="number"
            value={maxText}
          />
        </Stack>
      </Box>
    </Tooltip>
  );
}

/**
 * The dashboard period.
 *
 * Every other control in this bar narrows rows already in the browser, which is
 * free and so can be live. A period cannot work that way: asking for March
 * when April was loaded needs rows nobody fetched. So this one is a draft the
 * reader commits, and a calendar range that fired on each half would bill a
 * period nobody asked to see.
 *
 * Everyday periods are direct, labelled choices. The calendar only appears
 * after choosing Custom, so readers do not have to understand a saved
 * "default" before they can ask for the last seven days.
 */
